import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import {
  Tag,
  Bug,
  Lightbulb,
  Wrench,
  Calendar,
  User,
  RefreshCw,
  Rocket,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const TYPE_ICONS = { bug: Bug, feature: Lightbulb, improvement: Wrench };
const TYPE_COLORS = {
  bug: "text-red-400 border-red-500/40 bg-red-500/10",
  feature: "text-green-400 border-green-500/40 bg-green-500/10",
  improvement: "text-blue-400 border-blue-500/40 bg-blue-500/10",
};

const ChangelogPage = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchReleases = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/releases");
      setReleases(res.data || []);
      // Auto-expand the latest release
      if (res.data?.length > 0) {
        setExpanded({ [res.data[0].id]: true });
      }
    } catch {
      toast.error("Failed to load releases");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-5 p-6" data-testid="changelog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Tag className="w-6 h-6 text-orange-400" />
            Changelog
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Release history and deployed fixes
          </p>
        </div>
        <Button
          onClick={fetchReleases}
          variant="outline"
          size="sm"
          className="border-zinc-700"
          data-testid="refresh-changelog-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500 mx-auto" />
          <p className="text-sm text-zinc-600 mt-3">Loading releases...</p>
        </div>
      ) : releases.length === 0 ? (
        <div className="text-center py-20">
          <Rocket className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">
            No releases yet. Releases are created when you confirm deployed
            tasks.
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[19px] top-4 bottom-4 w-px bg-zinc-800" />

          <div className="space-y-4">
            {releases.map((release, idx) => {
              const isOpen = expanded[release.id];
              const tasks = release.tasks || [];
              const bugCount = tasks.filter((t) => t.type === "bug").length;
              const featureCount = tasks.filter(
                (t) => t.type === "feature"
              ).length;
              const improvementCount = tasks.filter(
                (t) => t.type === "improvement"
              ).length;

              return (
                <div key={release.id} className="relative pl-12">
                  {/* Timeline dot */}
                  <div
                    className={cn(
                      "absolute left-2.5 top-4 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10",
                      idx === 0
                        ? "bg-orange-500 border-orange-400"
                        : "bg-zinc-800 border-zinc-700"
                    )}
                  >
                    <Tag className="w-2.5 h-2.5 text-zinc-50" />
                  </div>

                  <Card
                    className={cn(
                      "bg-zinc-900/50 border-zinc-800 transition-all",
                      idx === 0 && "border-orange-500/30"
                    )}
                    data-testid={`release-card-${release.version}`}
                  >
                    <CardContent className="p-0">
                      {/* Header - clickable */}
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors rounded-t-lg"
                        onClick={() => toggleExpand(release.id)}
                        data-testid={`release-toggle-${release.version}`}
                      >
                        <div className="flex items-center gap-3">
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4 text-zinc-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-zinc-500" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "text-lg font-bold",
                                  idx === 0
                                    ? "text-orange-400"
                                    : "text-zinc-200"
                                )}
                              >
                                v{release.version}
                              </span>
                              {idx === 0 && (
                                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/40 text-[10px]">
                                  Latest
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(release.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {release.created_by_name}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {bugCount > 0 && (
                            <Badge
                              variant="outline"
                              className="text-red-400 border-red-500/40 bg-red-500/10 text-[10px]"
                            >
                              <Bug className="w-3 h-3 mr-0.5" />
                              {bugCount}
                            </Badge>
                          )}
                          {featureCount > 0 && (
                            <Badge
                              variant="outline"
                              className="text-green-400 border-green-500/40 bg-green-500/10 text-[10px]"
                            >
                              <Lightbulb className="w-3 h-3 mr-0.5" />
                              {featureCount}
                            </Badge>
                          )}
                          {improvementCount > 0 && (
                            <Badge
                              variant="outline"
                              className="text-blue-400 border-blue-500/40 bg-blue-500/10 text-[10px]"
                            >
                              <Wrench className="w-3 h-3 mr-0.5" />
                              {improvementCount}
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className="text-zinc-400 border-zinc-600 text-[10px]"
                          >
                            {release.tasks_count || tasks.length} tasks
                          </Badge>
                        </div>
                      </div>

                      {/* Expanded content */}
                      {isOpen && (
                        <div className="border-t border-zinc-800 p-4 space-y-3">
                          {release.notes && (
                            <p className="text-sm text-zinc-400 bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50">
                              {release.notes}
                            </p>
                          )}

                          {release.rework_count > 0 && (
                            <p className="text-xs text-orange-400">
                              {release.rework_count} task(s) sent back for
                              rework
                            </p>
                          )}

                          <div className="space-y-1.5">
                            {tasks.map((task) => {
                              const TypeIcon =
                                TYPE_ICONS[task.type] || Bug;
                              const typeColor =
                                TYPE_COLORS[task.type] ||
                                TYPE_COLORS.bug;

                              return (
                                <div
                                  key={task.id}
                                  className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-800/30 border border-zinc-800"
                                  data-testid={`release-task-${task.id}`}
                                >
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      "text-[9px] px-1.5 py-0 flex-shrink-0",
                                      typeColor
                                    )}
                                  >
                                    <TypeIcon className="w-2.5 h-2.5 mr-0.5" />
                                    {task.type}
                                  </Badge>
                                  <span className="text-sm text-zinc-300 flex-1">
                                    {task.title}
                                  </span>
                                  {task.affected_area && (
                                    <span className="text-[10px] text-zinc-600 flex-shrink-0">
                                      {task.affected_area}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangelogPage;
